const JSZip = require("jszip");

function decodeText(value = "") {
  return String(value)
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/p>|<\/div>|<\/li>/gi, "\n")
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&quot;/gi, '"')
    .replace(/&#39;|&apos;/gi, "'")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

function listItemText(item) {
  return decodeText(typeof item === "string" ? item : item?.content || item?.text);
}

function blockLines(block = {}) {
  const data = block.data || {};
  if (["paragraph", "header", "quote", "callout"].includes(block.type)) {
    return decodeText(data.text || data.title || data.content).split("\n");
  }
  if (block.type === "warning") {
    return [decodeText(data.title), decodeText(data.message)].filter(Boolean);
  }
  if (block.type === "code") return decodeText(data.code).split("\n");
  if (block.type === "linkTool") return [decodeText(data.link)];
  if (["list", "bulletedList", "numberedList"].includes(block.type)) {
    const ordered = block.type === "numberedList" || data.style === "ordered";
    return (data.items || []).map((item, index) =>
      `${ordered ? `${index + 1}.` : "•"} ${listItemText(item)}`.trim()
    );
  }
  if (block.type === "checklist") {
    return (data.items || []).map(
      (item) => `${item?.checked ? "☒" : "☐"} ${listItemText(item)}`.trim()
    );
  }
  if (["file", "image", "video", "audio"].includes(block.type)) {
    const label = decodeText(data.name || data.caption || data.url || data.link);
    return label ? [`Tệp đính kèm: ${label}`] : [];
  }
  return [];
}

function noteToParagraphs(note = {}) {
  const blocks = Array.isArray(note.content?.blocks) ? note.content.blocks : [];
  return blocks.flatMap(blockLines);
}

function noteBlocks(note = {}) {
  const blocks = Array.isArray(note.content?.blocks) ? [...note.content.blocks] : [];
  const representedImages = new Set(
    blocks
      .filter((block) => block.type === "image")
      .map((block) => block.data?.id || block.data?.url)
      .filter(Boolean)
  );
  for (const attachment of Array.isArray(note.attachments) ? note.attachments : []) {
    const type = String(attachment.mimeType || attachment.type || "").toLowerCase();
    const extension = String(attachment.extension || attachment.name?.split(".").pop() || "").toLowerCase();
    if (!type.startsWith("image/") && !["png", "jpg", "jpeg", "gif"].includes(extension)) continue;
    if (representedImages.has(attachment.id || attachment.url)) continue;
    blocks.push({ type: "image", data: attachment });
  }
  return blocks;
}

function escapeXml(value = "") {
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;");
}

function paragraphXml(text, { bold = false, size = 22 } = {}) {
  const runStyle = `<w:rPr>${bold ? "<w:b/>" : ""}<w:sz w:val="${size}"/><w:szCs w:val="${size}"/></w:rPr>`;
  return `<w:p><w:pPr><w:spacing w:after="120"/></w:pPr><w:r>${runStyle}<w:t xml:space="preserve">${escapeXml(text)}</w:t></w:r></w:p>`;
}

function safeFilename(value = "Ghi chú") {
  return (decodeText(value) || "Ghi chú").replace(/[\\/:*?"<>|]/g, "_").slice(0, 120);
}

function imageUrl(data = {}) {
  return data.url || data.cdnUrl || data.path || data.file?.url || "";
}

async function loadImageResource(url) {
  const response = await fetch(url, { credentials: "include" });
  if (!response.ok) throw new Error(`Không tải được ảnh (${response.status})`);
  const blob = await response.blob();
  const mimeType = blob.type || response.headers.get("content-type") || "";
  if (!/^image\/(png|jpe?g|gif)$/i.test(mimeType)) {
    throw new Error(`Định dạng ảnh chưa hỗ trợ: ${mimeType || "unknown"}`);
  }
  const bytes = await blob.arrayBuffer();
  let width = 640;
  let height = 400;
  if (typeof createImageBitmap === "function") {
    const bitmap = await createImageBitmap(blob);
    width = bitmap.width;
    height = bitmap.height;
    bitmap.close();
  }
  return { bytes, mimeType, width, height };
}

function imageExtension(mimeType = "") {
  if (/jpe?g/i.test(mimeType)) return "jpeg";
  if (/gif/i.test(mimeType)) return "gif";
  return "png";
}

function imageSize(width = 640, height = 400) {
  const maxWidth = 5486400;
  const maxHeight = 3657600;
  const naturalWidth = Math.max(width, 1) * 9525;
  const naturalHeight = Math.max(height, 1) * 9525;
  const scale = Math.min(1, maxWidth / naturalWidth, maxHeight / naturalHeight);
  return {
    width: Math.round(naturalWidth * scale),
    height: Math.round(naturalHeight * scale),
  };
}

function imageXml(image, index) {
  const size = imageSize(image.width, image.height);
  return `<w:p><w:pPr><w:spacing w:after="120"/></w:pPr><w:r><w:drawing><wp:inline distT="0" distB="0" distL="0" distR="0"><wp:extent cx="${size.width}" cy="${size.height}"/><wp:docPr id="${index}" name="Image ${index}"/><a:graphic><a:graphicData uri="http://schemas.openxmlformats.org/drawingml/2006/picture"><pic:pic><pic:nvPicPr><pic:cNvPr id="${index}" name="Image ${index}"/><pic:cNvPicPr/></pic:nvPicPr><pic:blipFill><a:blip r:embed="rId${index}"/><a:stretch><a:fillRect/></a:stretch></pic:blipFill><pic:spPr><a:xfrm><a:off x="0" y="0"/><a:ext cx="${size.width}" cy="${size.height}"/></a:xfrm><a:prstGeom prst="rect"><a:avLst/></a:prstGeom></pic:spPr></pic:pic></a:graphicData></a:graphic></wp:inline></w:drawing></w:r></w:p>`;
}

async function buildNoteDocx(note, outputType = "blob", options = {}) {
  const title = decodeText(note?.title) || "Ghi chú";
  const blocks = noteBlocks(note);
  const imageLoader = options.imageLoader || loadImageResource;
  const images = [];
  const content = [];
  for (const block of blocks) {
    const url = block.type === "image" ? imageUrl(block.data) : "";
    if (url) {
      try {
        const image = await imageLoader(url);
        const index = images.length + 1;
        images.push({ ...image, index });
        content.push(imageXml(image, index));
        continue;
      } catch (error) {
        content.push(paragraphXml(`Hình ảnh: ${decodeText(block.data?.name || url)}`));
        continue;
      }
    }
    content.push(...blockLines(block).map((line) => paragraphXml(line)));
  }
  const documentXml = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<w:document xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships" xmlns:wp="http://schemas.openxmlformats.org/drawingml/2006/wordprocessingDrawing" xmlns:a="http://schemas.openxmlformats.org/drawingml/2006/main" xmlns:pic="http://schemas.openxmlformats.org/drawingml/2006/picture"><w:body>
${paragraphXml(title, { bold: true, size: 32 })}
${content.join("\n")}
<w:sectPr><w:pgSz w:w="11906" w:h="16838"/><w:pgMar w:top="1440" w:right="1440" w:bottom="1440" w:left="1440"/></w:sectPr>
</w:body></w:document>`;

  const zip = new JSZip();
  zip.file(
    "[Content_Types].xml",
    '<?xml version="1.0" encoding="UTF-8"?><Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types"><Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/><Default Extension="xml" ContentType="application/xml"/><Default Extension="png" ContentType="image/png"/><Default Extension="jpeg" ContentType="image/jpeg"/><Default Extension="gif" ContentType="image/gif"/><Override PartName="/word/document.xml" ContentType="application/vnd.openxmlformats-officedocument.wordprocessingml.document.main+xml"/></Types>'
  );
  zip.folder("_rels").file(
    ".rels",
    '<?xml version="1.0" encoding="UTF-8"?><Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="word/document.xml"/></Relationships>'
  );
  const word = zip.folder("word");
  word.file("document.xml", documentXml);
  word.folder("_rels").file(
    "document.xml.rels",
    `<?xml version="1.0" encoding="UTF-8"?><Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">${images.map((image) => `<Relationship Id="rId${image.index}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/image" Target="media/image${image.index}.${imageExtension(image.mimeType)}"/>`).join("")}</Relationships>`
  );
  const media = word.folder("media");
  images.forEach((image) => media.file(`image${image.index}.${imageExtension(image.mimeType)}`, image.bytes));
  return zip.generateAsync({ type: outputType, mimeType: "application/vnd.openxmlformats-officedocument.wordprocessingml.document" });
}

async function exportNoteToWord(note) {
  if (!note) return;
  const blob = await buildNoteDocx(note);
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `${safeFilename(note.title)}.docx`;
  link.click();
  URL.revokeObjectURL(url);
}

module.exports = { buildNoteDocx, exportNoteToWord, noteToParagraphs, safeFilename };
